import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, Mail, MessageSquare, Webhook, Save } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';

const WorkflowNotificationSettings = ({ workflowId }) => {
  const [settings, setSettings] = useState({
    channels: ['email'],
    slack_webhook_url: '',
    discord_webhook_url: '',
    custom_webhook_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [workflowId]);

  const fetchSettings = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_URL}/api/workflows/${workflowId}/notification-settings`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/api/workflows/${workflowId}/notification-settings`,
        settings,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Notification settings saved!');
    } catch (error) {
      toast.error('Failed to save notification settings');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (channel) => {
    setSettings(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  if (loading) {
    return <div className="animate-pulse">Loading notification settings...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Error Notifications
        </CardTitle>
        <CardDescription>
          Get notified when this workflow fails
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email Notifications */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-muted-foreground">Send to your registered email</p>
            </div>
          </div>
          <Switch
            checked={settings.channels.includes('email')}
            onCheckedChange={() => toggleChannel('email')}
          />
        </div>

        {/* Slack Notifications */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium">Slack Notifications</p>
                <p className="text-sm text-muted-foreground">Send to Slack channel</p>
              </div>
            </div>
            <Switch
              checked={settings.channels.includes('slack')}
              onCheckedChange={() => toggleChannel('slack')}
            />
          </div>
          
          {settings.channels.includes('slack') && (
            <Input
              placeholder="https://hooks.slack.com/services/..."
              value={settings.slack_webhook_url || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, slack_webhook_url: e.target.value }))}
              className="ml-13"
            />
          )}
        </div>

        {/* Discord Notifications */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="font-medium">Discord Notifications</p>
                <p className="text-sm text-muted-foreground">Send to Discord channel</p>
              </div>
            </div>
            <Switch
              checked={settings.channels.includes('discord')}
              onCheckedChange={() => toggleChannel('discord')}
            />
          </div>
          
          {settings.channels.includes('discord') && (
            <Input
              placeholder="https://discord.com/api/webhooks/..."
              value={settings.discord_webhook_url || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, discord_webhook_url: e.target.value }))}
              className="ml-13"
            />
          )}
        </div>

        {/* Custom Webhook */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Webhook className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium">Custom Webhook</p>
                <p className="text-sm text-muted-foreground">Send to custom endpoint</p>
              </div>
            </div>
            <Switch
              checked={settings.channels.includes('webhook')}
              onCheckedChange={() => toggleChannel('webhook')}
            />
          </div>
          
          {settings.channels.includes('webhook') && (
            <Input
              placeholder="https://your-api.com/webhook"
              value={settings.custom_webhook_url || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, custom_webhook_url: e.target.value }))}
              className="ml-13"
            />
          )}
        </div>

        {/* Save Button */}
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="w-full"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Notification Settings'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default WorkflowNotificationSettings;
